import React, { createContext, useCallback, useContext, useMemo } from "react";
import { usePartialState } from "../helper/Hooks/usePartialState";
import { useAlertHelpers } from "../helper/Hooks/useAlertHelpers";

export interface AccordionState {
  patient: {
    current: string;
    survey: string;
    info: string;
    report: string;
    clinics: string;
  };
  clinician: {
    myPatients: string;
    clinics: string;
    info: string;
  };
  admin: {
    patients: string;
    employees: string;
    clinic: string;
  };
}

//set eventKey for each Accordion
export const ACCORDION_EVENT_KEYS = {
  patient: {
    current: "0",
    info: "1",
    survey: "2",
    report: "3",
    clinics: "4",
  },
  clinician: {
    myPatients: "0",
    clinics: "1",
    info: "2",
  },
  admin: {
    patients: "0",
    employees: "1",
    clinic: "2",
  },
} as const;

interface AccordionContextType {
  state: AccordionState;
  handleAccordion: (params: { input?: string; userType?: string }) => void;
  closeAll: (userType: string) => void;
}

//The initial state is to set all accordion closed
const initialAccordionState: AccordionState = {
  patient: {
    current: "-1",
    info: "-1",
    survey: "-1",
    report: "-1",
    clinics: "-1",
  },
  clinician: {
    myPatients: "-1",
    clinics: "-1",
    info: "-1",
  },
  admin: {
    patients: "-1",
    employees: "-1",
    clinic: "-1",
  },
};

const AccordionContext = createContext<AccordionContextType | null>(null);

interface AccordionProviderProps {
  children: React.ReactNode;
}

/**
 * Provides accordion state management for different user types
 * @example
 * ```tsx
 * const { state, handleAccordion } = useAccordion();
 * handleAccordion({ userType: 'patient', input: 'current' });
 * ```
 */
export const AccordionProvider = ({ children }: AccordionProviderProps) => {
  const [state, updateState] = usePartialState(initialAccordionState);
  const { showAlertModal } = useAlertHelpers();

  const handleAccordion = useCallback(
    (params: { userType?: string; input?: string }) => {
      const { input, userType } = params;

      // Show alert with invalid userType
      if (!userType || !(userType in state)) {
        showAlertModal(
          `Ungültiger Benutzertype. Erlaubte Werte sind: patient, clinician, admin.`,
          "warning",
        );
        console.error(`Invalid userType. Available types:`, Object.keys(state));
        return;
      }

      //check input
      if (!input || !(input in state[userType as keyof AccordionState])) {
        showAlertModal(
          `Ungültiger Accordion-Bereich für Benutzertyp ${userType}.`,
          "warning",
        );
        console.error(
          `Invalid input for userType: ${userType}. Available inputs:`,
          Object.keys(state[userType as keyof AccordionState]),
        );
        return;
      }

      const currentUserState = state[userType];
      const currentValue =
        currentUserState[input as keyof typeof currentUserState];

      //get eventKey for this accordion
      const eventKey = ACCORDION_EVENT_KEYS[userType]?.[input] || "-1";

      //set a new state
      const newUserState: Record<string, string> = {};

      //switch the current accordion state
      for (const key in currentUserState) {
        if (key === input) {
          newUserState[key] = currentValue === "-1" ? eventKey : "-1";
        } else {
          newUserState[key] = "-1";
        }
      }

      updateState({
        [userType]: newUserState,
      });
    },
    [state, updateState, showAlertModal],
  );

  const closeAll = useCallback(
    (userType: string) => {
      if (!(userType in state)) {
        console.error(`Invalid userType: ${userType}`);
        return;
      }

      const currentUserState = state[userType as keyof AccordionState];
      const closedState: Record<string, string> = {};

      for (const key in currentUserState) {
        closedState[key] = "-1";
      }

      updateState({
        [userType]: closedState,
      });
    },
    [state, updateState],
  );

  const contextValue = useMemo(
    () => ({
      state,
      handleAccordion,
      closeAll,
    }),
    [state, handleAccordion, closeAll],
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      {children}
    </AccordionContext.Provider>
  );
};

// Custom hook for this context
export const useAccordion = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error("useAccordion must be used within AccordionProvider");
  }
  return context;
};
